import Css from "../../style.module.scss";

import { FormElement } from "nlib/common/Form";
import { getActiveOrganization } from "selectors/organizations";
import {
  getAllBusinessesData,
  getSelectedBusinessClasses,
  getSelectedBusinessId,
  getSelectedBusinessLocations,
  getSelectedBusinessProjects,
  getSelectedBusinessTaxRates
} from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useMainApi } from "hooks";
import { useSelector } from "react-redux";
import Checkbox from "nlib/ui/Checkbox";
import Countries from "const/Countries";
import ExcludedWords from "./lib/ExcludedWords";
import FormContext from "contexts/FormContext";
import IntegrationServices from "const/IntegrationServices";
import React, { useCallback, useContext, useMemo } from "react";
import SelectExcludedAccounts from "./lib/SelectExcludedAccounts";
import UiRoutes from "const/UiRoutes";
import UncategorizedTransactions from "../UncategorizedTab/lib/UncategorizedTransactions";
import classNames from "classnames";

const AiBookkeeperTabProcessing = ({ disabled, businessId, integrationService, ignoreUncategorizedTransactions }) => {
  const { messages } = useSelector(getTextsData);

  const { countryCode } = useSelector(getActiveOrganization);

  const {
    values: { processOnlyBankFeed, rpaExcludedAccountsToggle, rpaExcludedWordsToggle },
    onChange: onFormChange
  } = useContext(FormContext);

  const selectedBusinessId = useSelector(getSelectedBusinessId);

  const businessesData = useSelector(getAllBusinessesData);

  const classes = useSelector(getSelectedBusinessClasses);

  const taxRates = useSelector(getSelectedBusinessTaxRates);

  const locations = useSelector(getSelectedBusinessLocations);

  const projects = useSelector(getSelectedBusinessProjects);

  const selectedBusinessData = useMemo(() => {
    return businessesData.find(({ id }) => id === businessId) || {};
  }, [businessId, businessesData]);

  const [
    {
      extraData: { quickBooksClassification } = {},
      settings: { uncategorizedCategoryNames = [] } = {}
    }, fetching
  ] = useMainApi({
    fetchCondition: selectedBusinessId !== businessId,
    initialData: selectedBusinessData,
    parameters: [`${UiRoutes.BUSINESSES}/${businessId}`]
  });

  const quickBooksBusiness = integrationService === IntegrationServices.QUICK_BOOKS.value;

  const usCountry = countryCode === Countries.US;

  const projectsToggleLabel = useMemo(() => {
    return projects.some(({ customer }) => customer)
      ? (projects.some(({ customer }) => !customer)
        ? messages.rpaProcessingProjectOrCustomerToggleLabelText : messages.rpaProcessingCustomerToggleLabelText)
      : messages.rpaProcessingProjectToggleLabelText;
  }, [projects, messages]);

  const projectsToggleDescription = useMemo(() => {
    return projects.some(({ customer }) => customer)
      ? (projects.some(({ customer }) => !customer)
        ? messages.rpaProcessingProjectOrCustomerToggleDescription : messages.rpaProcessingCustomerToggleDescription)
      : messages.rpaProcessingProjectToggleDescription;
  }, [projects, messages]);

  const handleRpaExcludedAccountsToggleChange = useCallback((value) => {
    onFormChange({ name: "rpaExcludedAccountsToggle", value: !value });
  }, [onFormChange]);

  const handleRpaExcludedWordsToggleChange = useCallback((value) => {
    onFormChange({ name: "rpaExcludedWordsToggle", value: !value });
  }, [onFormChange]);

  return (
    <>
      {quickBooksBusiness && !ignoreUncategorizedTransactions && (
        <div className={classNames(Css.row, !processOnlyBankFeed && Css.rowWithRelatedContent)}>
          <div className={Css.col}>
            <FormElement
              toggle
              name="processOnlyBankFeed"
              readOnly={disabled || (fetching && selectedBusinessId !== businessId)}
              element={Checkbox}>
              <span>{messages.uncategorizedToggleLabelText}</span>
            </FormElement>
            <div className={Css.description}>{messages.uncategorizedToggleDescription}</div>
          </div>
        </div>
      )}
      {quickBooksBusiness && !ignoreUncategorizedTransactions && !processOnlyBankFeed && (
        <div className={`${Css.row} ${Css.toggleRelatedContent}`}>
          <div className={Css.col}>
            <div className={Css.relatedInputLabel}>{messages.settingsUncategorizedLabel}</div>
            <UncategorizedTransactions
              disabled={disabled || (fetching && selectedBusinessId !== businessId)}
              classification={quickBooksClassification}
              uncategorizedCategoryNames={uncategorizedCategoryNames} />
          </div>
        </div>
      )}
      <div className={classNames(Css.row, rpaExcludedAccountsToggle && Css.rowWithRelatedContent)}>
        <div className={Css.col}>
          <Checkbox
            toggle
            checked={!rpaExcludedAccountsToggle}
            onChange={handleRpaExcludedAccountsToggleChange}>
            {messages.processAllAccountsToggleLabelText}
          </Checkbox>
          <div className={Css.description}>{messages.processAllAccountsToggleDescription}</div>
        </div>
      </div>
      {rpaExcludedAccountsToggle && (
        <div className={`${Css.row} ${Css.toggleRelatedContent}`}>
          <div className={Css.col}>
            <div className={Css.relatedInputLabel}>{messages.settingsExcludedAccountsText}</div>
            <FormElement
              readOnly={disabled}
              businessId={businessId}
              integrationService={integrationService}
              name="rpaExcludedAccounts"
              element={SelectExcludedAccounts} />
          </div>
        </div>
      )}
      <div className={classNames(Css.row, rpaExcludedWordsToggle && Css.rowWithRelatedContent)}>
        <div className={Css.col}>
          <Checkbox
            toggle
            checked={!rpaExcludedWordsToggle}
            onChange={handleRpaExcludedWordsToggleChange}>
            {messages.processAllTransactionsToggleLabelText}
          </Checkbox>
          <div className={Css.description}>{messages.processAllTransactionsToggleDescription}</div>
        </div>
      </div>
      {rpaExcludedWordsToggle && (
        <div className={`${Css.row} ${Css.toggleRelatedContent}`}>
          <div className={Css.col}>
            <div className={Css.relatedInputLabel}>{messages.rpaExcludedWordsLabel}</div>
            <FormElement
              readOnly={disabled}
              name="rpaExcludedWords"
              element={ExcludedWords} />
          </div>
        </div>
      )}
      {!!classes.length && (
        <div className={Css.row}>
          <div className={Css.col}>
            <FormElement toggle readOnly={disabled} name="rpaClassEnabled" element={Checkbox}>
              <span>{messages.rpaProcessingClassToggleLabelText}</span>
            </FormElement>
            <div className={Css.description}>{messages.rpaProcessingClassToggleDescription}</div>
          </div>
        </div>
      )}
      {!!locations.length && (
        <div className={Css.row}>
          <div className={Css.col}>
            <FormElement toggle readOnly={disabled} name="rpaLocationEnabled" element={Checkbox}>
              <span>{messages.rpaProcessingLocationToggleLabelText}</span>
            </FormElement>
            <div className={Css.description}>{messages.rpaProcessingLocationToggleDescription}</div>
          </div>
        </div>
      )}
      {!!projects.length && (
        <div className={Css.row}>
          <div className={Css.col}>
            <FormElement toggle readOnly={disabled} name="rpaProjectEnabled" element={Checkbox}>
              <span>{projectsToggleLabel}</span>
            </FormElement>
            <div className={Css.description}>{projectsToggleDescription}</div>
          </div>
        </div>
      )}
      {!usCountry && !!taxRates.length && (
        <div className={Css.row}>
          <div className={Css.col}>
            <FormElement toggle readOnly={disabled} name="rpaTaxRateEnabled" element={Checkbox}>
              <span>{messages.rpaProcessingTaxRateToggleLabelText}</span>
            </FormElement>
            <div className={Css.description}>{messages.rpaProcessingTaxRateToggleDescription}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default AiBookkeeperTabProcessing;
